import React, { useState } from "react";
import classNames from "classnames";
import useNoiseInfo from "../../store/useNoiseInfo";
import RadioGroup from "../../../../components/RadioGroup";
import NoiseInfoImage from "../../NoiseInfoImage";
import { NOISE_RECURRENT, toNoiseRecurrent } from "../../config/constants";
import { isNoiseMarked } from "../../utils/issueValidation";
import useNoiseCodes from "./useNoiseCodes";
import styles from "./index.module.scss";

/**
 * 异响反馈表单（问题描述页内，仅异响子项渲染）。
 * 字段：能否重现异响 / 选择异响位置入口。
 * 数据存于 useNoiseInfo；「否」通知文案来自 useNoiseCodes。描述文本与附件由外层按子项统一处理，不在此。
 *
 * readOnly 只读态：复用同一套编辑布局，整体禁用交互——
 * - 异响位置：保留「查看标记」入口（唯一可点动作），打开 NoiseInfoImage 查看态；
 * - 能否重现单选：不可点击，仅展示选中态。
 */
export default function NoiseFeed({ readOnly = false }: { readOnly?: boolean }) {
  const { noiseInfo, setNoiseFields } = useNoiseInfo();
  const { noiseRecurrent } = noiseInfo;
  const { notice } = useNoiseCodes({ silent: readOnly });

  const [markVisible, setMarkVisible] = useState(false);

  // 是否已标注异响位置（已选点或已上传合成图）
  const noiseMarked = isNoiseMarked(noiseInfo);

  return (
    <div className={styles.noiseExtra}>
      {/* 异响位置：编辑=去标记/修改标记（打开标注弹窗，始终展示以便标记）；
          只读=查看标记（打开查看态弹窗），仅在已标注时展示，避免点开空白图 */}
      {(!readOnly || noiseMarked) && (
        <div className={styles.rowLine} onClick={() => setMarkVisible(true)}>
          <span className={styles.rowLabel}>
            {!readOnly && <span className={styles.reqStar}>*</span>}
            {readOnly ? "异响位置" : "选择异响位置"}
          </span>
          <div className={styles.rowValue}>
            <span
              className={classNames(styles.markLink, {
                [styles.markLinkEmpty]: !readOnly && !noiseMarked,
              })}
            >
              {readOnly ? "查看标记" : noiseMarked ? "修改标记" : "去标记"}
            </span>
            <i className={styles.rowArrow} />
          </div>
        </div>
      )}

      {/* 能否重现异响（只读态禁用单选） */}
      <div className={styles.rowLine}>
        <span className={styles.rowLabel}>
          {!readOnly && <span className={styles.reqStar}>*</span>}
          能否重现异响
        </span>
        <RadioGroup
          options={[
            { label: "是", value: NOISE_RECURRENT.YES },
            { label: "否", value: NOISE_RECURRENT.NO },
          ]}
          value={noiseRecurrent}
          disabled={readOnly}
          onChange={(v) => setNoiseFields({ noiseRecurrent: toNoiseRecurrent(v) })}
        />
      </div>

      {/* 否 → 通知文案（编辑态引导，只读态不展示） */}
      {!readOnly && noiseRecurrent === NOISE_RECURRENT.NO && notice && (
        <div className={styles.notice}>{notice}</div>
      )}

      {/* 异响位置弹窗：编辑态标注 / 只读态查看（readOnly 透传） */}
      <NoiseInfoImage
        visible={markVisible}
        onClose={() => setMarkVisible(false)}
        readOnly={readOnly}
      />
    </div>
  );
}
